import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Mic, Pause, Play } from 'lucide-react';
import './chat-audio-message.css';

const BAR_COUNT = 36;
const WAVE_HEIGHT = 28;

let stopActiveAudio: (() => void) | null = null;

function hashString(value: string): number {
  let hash = 2166136261;

  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  return hash >>> 0;
}

function buildWaveform(seed: string): number[] {
  let value = hashString(seed) || 1;
  const bars: number[] = [];

  for (let index = 0; index < BAR_COUNT; index += 1) {
    value = (Math.imul(value, 1664525) + 1013904223) >>> 0;
    bars.push(0.2 + (value / 4294967296) * 0.8);
  }

  return bars;
}

function formatTime(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(total / 60);
  const remainingSeconds = total % 60;

  return `${minutes}:${String(remainingSeconds).padStart(2, '0')}`;
}

export interface ChatAudioMessageProps {
  src: string;
  avatarPath?: string;
  avatarName?: string;
  isOwn?: boolean;
  footerRight?: React.ReactNode;
}

export default function ChatAudioMessage({
  src,
  avatarPath,
  avatarName,
  isOwn = false,
  footerRight,
}: ChatAudioMessageProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [current, setCurrent] = useState(0);
  const waveform = useMemo(() => buildWaveform(src), [src]);

  const pauseSelf = useCallback(() => {
    audioRef.current?.pause();
    setPlaying(false);
  }, []);

  useEffect(() => {
    const audio = new Audio(src);
    audio.preload = 'metadata';
    audioRef.current = audio;

    const handleLoadedMetadata = () => {
      setDuration(Number.isFinite(audio.duration) ? audio.duration : 0);
    };
    const handleTimeUpdate = () => setCurrent(audio.currentTime);
    const handleEnded = () => {
      setPlaying(false);
      setCurrent(0);
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('durationchange', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);
    audio.load();

    return () => {
      audio.pause();
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('durationchange', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);

      if (stopActiveAudio === pauseSelf) stopActiveAudio = null;
      if (audioRef.current === audio) audioRef.current = null;
    };
  }, [pauseSelf, src]);

  const togglePlay = async () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (playing) {
      pauseSelf();
      return;
    }

    if (stopActiveAudio && stopActiveAudio !== pauseSelf) stopActiveAudio();
    stopActiveAudio = pauseSelf;

    try {
      await audio.play();
      setPlaying(true);
    } catch {
      setPlaying(false);
    }
  };

  const handleSeek = (event: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;

    const bounds = event.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (event.clientX - bounds.left) / bounds.width));
    const nextTime = ratio * duration;

    audio.currentTime = nextTime;
    setCurrent(nextTime);
  };

  const progress = duration ? Math.min(1, current / duration) : 0;
  const playedBars = Math.round(progress * BAR_COUNT);
  const fallbackName = avatarName?.trim().charAt(0).toUpperCase() || '?';

  return (
    <div
      className={`chat-audio-message${isOwn ? ' is-own' : ''}`}
      onMouseDown={event => event.stopPropagation()}
      onMouseUp={event => event.stopPropagation()}
      onMouseMove={event => event.stopPropagation()}
      onDoubleClick={event => event.stopPropagation()}
      onTouchStart={event => event.stopPropagation()}
      onTouchMove={event => event.stopPropagation()}
      onTouchEnd={event => event.stopPropagation()}
    >
      <div className="chat-audio-main-row">
        <div className="chat-audio-avatar-wrap">
          <img
            className="chat-audio-avatar"
            src={avatarPath || '/img/img-account.png'}
            alt={avatarName || 'Avatar'}
            onError={event => {
              event.currentTarget.onerror = null;
              event.currentTarget.src = '/img/img-account.png';
            }}
          />
          {!avatarPath && <span className="chat-audio-avatar-fallback">{fallbackName}</span>}
          <span className="chat-audio-mic-badge" aria-hidden="true">
            <Mic size={11} strokeWidth={2.5} />
          </span>
        </div>

        <button
          type="button"
          className="chat-audio-play-button"
          onClick={togglePlay}
          aria-label={playing ? 'Pausar áudio' : 'Reproduzir áudio'}
        >
          {playing ? <Pause size={23} fill="currentColor" /> : <Play size={23} fill="currentColor" />}
        </button>

        <div
          className="chat-audio-wave-touch"
          onClick={handleSeek}
          role="slider"
          tabIndex={0}
          aria-label="Progresso do áudio"
          aria-valuemin={0}
          aria-valuemax={duration}
          aria-valuenow={current}
          onKeyDown={event => {
            if (!duration || (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')) return;
            event.preventDefault();
            const step = event.key === 'ArrowRight' ? 5 : -5;
            const nextTime = Math.min(duration, Math.max(0, current + step));
            if (audioRef.current) audioRef.current.currentTime = nextTime;
            setCurrent(nextTime);
          }}
        >
          <div className="chat-audio-wave" aria-hidden="true">
            {waveform.map((height, index) => (
              <span
                className={`chat-audio-wave-bar${index < playedBars ? ' is-played' : ''}`}
                key={`${src}-${index}`}
                style={{ height: Math.max(3, height * WAVE_HEIGHT) }}
              />
            ))}
          </div>
          <span
            className="chat-audio-progress-dot"
            aria-hidden="true"
            style={{ left: `calc(${progress * 100}% - ${progress * 12}px)` }}
          />
        </div>
      </div>

      <div className="chat-audio-footer">
        <span className="chat-audio-duration">
          {formatTime(playing || current > 0 ? current : duration)}
        </span>
        {footerRight}
      </div>
    </div>
  );
}
